import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, StatusChip, Loading } from "@/components/common";
import { CheckCircle2, Truck, ClipboardList, LogIn, PackageCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Each HUB step maps to an authoritative ActiveDay flag (persists across nav/refresh).
const HUB_STEPS = [
  { key: "clock_in", flag: "clock_in_completed", label: "Clock In", icon: LogIn },
  { key: "crew_brief", flag: "crew_brief_completed", label: "Crew Brief", icon: ClipboardList },
  { key: "equipment_checkout", flag: "equipment_checkout_completed", label: "Equipment Checkout", icon: PackageCheck },
  { key: "vehicle_load", flag: "vehicle_load_completed", label: "Vehicle / Load Confirmation", icon: Truck },
  { key: "ready", flag: "ready_to_depart_completed", label: "Ready to Depart", icon: CheckCircle2 },
];

const STAGE_LABEL = {
  arrival: "Arrival", property_scan: "Property Scan", estimate: "Estimate",
  customer_authorization: "Authorization", payment: "Payment", prep: "Prep",
  service: "Service", mid_job_qc: "Mid-Job QC", final_qc: "Final QC",
  before_after_photos: "Photos", customer_walkthrough: "Walkthrough", closeout: "Closeout", closed: "Closed",
};

function jobAction(j) {
  if (j.stage === "closed") return null;
  if (j.payment_state === "unpaid" && (j.quote?.final_price)) return { label: "Payment needed", tone: "#F59E0B" };
  if (!j.quote?.final_price) return { label: "Needs quote", tone: "#F59E0B" };
  if (j.service_locked) return { label: "Service locked", tone: "#EF4444" };
  return { label: "In progress", tone: "var(--accent)" };
}

export default function Today() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [day, setDay] = useState(null);          // authoritative ActiveDay record
  const [checkout, setCheckout] = useState(null);
  const [dep, setDep] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [j, d, dp] = await Promise.all([api.get("/jobs"), api.get("/active-day"), api.get("/equipment/departure-status").catch(() => ({ data: null }))]);
    setJobs(j.data);
    setDay(d.data);
    setDep(dp.data);
  };
  useEffect(() => { load(); }, []);

  const persistStep = async (flag) => {
    const { data } = await api.patch("/active-day/step", { step: flag, value: true });
    setDay(data);
  };

  const runStep = async (s) => {
    try {
      if (s.key === "clock_in") { try { await api.post("/time/clock", { action: "in" }); } catch {} }
      if (s.key === "equipment_checkout") {
        const { data } = await api.post("/equipment/checkout", { crew_id: user.crew_id });
        setCheckout(data); setDep(data);
        if (!data.can_depart) { toast.warning("Critical equipment review pending — Manager/Owner must resolve"); return; }
      }
      await persistStep(s.flag);
      toast.success(`${s.label} complete`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not complete step");
    }
  };

  if (!jobs || !day) return <Loading label="Loading today" />;
  const isDone = (s) => !!day[s.flag];
  const filtered = jobs.filter((j) => filter === "all" ? true : filter === "complete" ? j.stage === "closed" : j.stage !== "closed");
  const activeCount = jobs.filter((j) => j.stage !== "closed").length;
  const needsAction = jobs.filter((j) => jobAction(j)?.label === "Payment needed" || jobAction(j)?.label === "Needs quote").length;

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="HUB001 start of day + assigned jobs">Today</SectionTitle>

      {/* HUB001 start */}
      <div className="sg-card p-5 mb-6" data-testid="hub-start">
        <div className="font-head text-xl font-bold mb-4">HUB001 · Start of Day</div>
        <div className="flex flex-col sm:flex-row sm:items-stretch gap-3">
          {HUB_STEPS.map((s, i) => {
            const complete = isDone(s);
            const prevDone = i === 0 || isDone(HUB_STEPS[i - 1]);
            const depState = dep?.state;               // live: ready | review_required | not_ready
            const canDepart = dep ? dep.can_depart : true;
            const reviewing = depState && depState !== "ready";
            const blocked = s.key === "ready" && !canDepart;   // Tech can NEVER self-clear this
            return (
              <div key={s.key} className={`flex-1 sg-card-elev p-4 ${complete && !blocked ? "tile-active" : ""}`} data-testid={`hub-step-${s.key}`}>
                <s.icon size={22} style={{ color: complete && !blocked ? "var(--accent)" : "var(--txt2)" }} />
                <div className="font-head font-bold mt-2 text-sm">{i + 1}. {s.label}</div>
                {s.key === "equipment_checkout" && dep && (
                  <div className="text-xs mt-1" style={{ color: dep.display_state === "ready" ? "#10B981" : dep.display_state === "not_ready" ? "#EF4444" : "#F59E0B" }}>
                    {dep.display_state === "ready" ? "All critical ready"
                      : dep.display_state === "override_approved" ? "Departure approved by override"
                      : dep.display_state === "review_required" ? "REVIEW REQUIRED — pending Manager/Owner"
                      : `${dep.blocking.length} blocking`}
                  </div>
                )}
                {s.key === "ready" && blocked ? (
                  <div className="mt-3" data-testid="hub-ready-locked"><StatusChip status="locked" label="Locked — Equipment Review" /></div>
                ) : s.key === "equipment_checkout" && complete && reviewing ? (
                  <div className="mt-3" data-testid="hub-review-equipment_checkout"><StatusChip status="warning" label="Review Required" /></div>
                ) : !complete ? (
                  <button className="sg-btn mt-3 w-full" disabled={!prevDone || blocked} onClick={() => runStep(s)} data-testid={`hub-do-${s.key}`}>
                    {s.key === "ready" ? "Depart" : "Confirm"}
                  </button>
                ) : (
                  <div className="mt-3" data-testid={`hub-done-${s.key}`}><StatusChip status="ready" label="Done" /></div>
                )}
              </div>
            );
          })}
        </div>
        {dep && !dep.can_depart && (
          <div className="mt-3 text-sm p-3 rounded-lg" style={{ background: dep.state === "review_required" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)", color: dep.state === "review_required" ? "#F59E0B" : "#EF4444" }} data-testid="hub-depart-block">
            {dep.state === "review_required"
              ? "REVIEW REQUIRED — a critical equipment report is pending Manager/Owner approval. Ready to Depart is locked until it is resolved."
              : `Blocking (critical): ${dep.blocking.map((b) => `${b.name} (${b.status})`).join(", ")} — Manager override required in Equipment.`}
          </div>
        )}
      </div>

      {/* Job feed summary */}
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="today-summary">
        <span className="chip" style={{ background: "var(--bg-elev)", color: "var(--txt2)", borderColor: "var(--brd)" }}>{jobs.length} assigned</span>
        <span className="chip" style={{ background: "var(--bg-elev)", color: "var(--accent)", borderColor: "var(--brd)" }}>{activeCount} active</span>
        {needsAction > 0 && <span className="chip" data-testid="today-action-count" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B", borderColor: "#F59E0B", fontWeight: 700 }}>{needsAction} need action</span>}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4">
        {["all", "active", "complete"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className="chip" style={{ cursor: "pointer", textTransform: "capitalize", background: filter === f ? "var(--accent)" : "var(--bg-elev)", color: filter === f ? "#fff" : "var(--txt2)", borderColor: filter === f ? "var(--accent)" : "var(--brd)" }}>
            {f}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="today-jobs">
        {filtered.length === 0 && <div className="text-sm" style={{ color: "var(--txt2)" }} data-testid="today-empty">No jobs in this filter yet.</div>}
        {filtered.map((j) => {
          const action = jobAction(j);
          return (
            <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-${j.id}`}
              className="sg-card p-5 text-left hover:border-[var(--accent)]" style={{ transition: "border-color .12s" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-head text-lg font-bold">{j.title || "Untitled Job"}</span>
                <ChevronRight size={20} style={{ color: "var(--txt2)" }} />
              </div>
              {(j.customer_name || j.address) && <div className="text-xs mb-2" style={{ color: "var(--txt2)" }}>{j.customer_name}{j.address ? ` · ${j.address}` : ""}</div>}
              <div className="flex flex-wrap gap-2 items-center">
                {j.is_test && <span className="chip" data-testid={`test-badge-${j.id}`} style={{ background: "rgba(245,158,11,0.18)", color: "#F59E0B", borderColor: "#F59E0B", fontWeight: 700 }}>TEST</span>}
                <StatusChip status={j.stage === "closed" ? "ready" : "in_progress"} label={STAGE_LABEL[j.stage] || j.stage} />
                <StatusChip status={j.payment_state} />
                {j.service_locked && <StatusChip status="locked" label="Service Locked" />}
                {action && <span className="chip" data-testid={`job-action-${j.id}`} style={{ background: "transparent", color: action.tone, borderColor: action.tone, fontWeight: 700 }}>{action.label}</span>}
              </div>
              <div className="text-sm mt-3 font-mono" style={{ color: "var(--txt2)" }}>
                Collected ${(j.collected_revenue || 0).toLocaleString()}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
